<template>
  <MDBSideNav right v-model="drawerReportState" id="ProjectTaggingReportFilter" mode="push" contentSelector="#ProjectTagging" :sidenavWidth="354">
    <div class="drawer-container">
      <div class="drawer-header">
        Filter
        <span class="SproutIcon SproutIcon-cross-regular" @click="handleDrawerClose"></span>
      </div>
      <div>
        <div class="drawer-filter">
          <SPAInput
            label="Project"
            :hasError="isAssigneesCountZero"
            errorMessage="No employees assigned. Please select another project."
            v-model="showProject"
            readonly
            @click.stop.prevent="handleDrawerClick('project')"
          />
          <SPAInput
            label="Employee"
            v-model="showEmployees"
            :readonly="!isConfirmedProjectEmpty && !isAssigneesCountZero && isConfirmedProjectEmpty"
            @click.stop.prevent="handleDrawerClick('employee')"
            :disabled="isConfirmedProjectEmpty || isAssigneesCountZero"
          />
        </div>
        <div class="drawer-date-range">
          DATE RANGE
          <SPADatepicker
            label="Date From"
            v-model="initialDateFrom.date"
            :endDate="initialDateTo.date"
            standardFormat
            checkIfIsWithin31Day
            @hasError="initialDateFrom.isError = $event"
            @input="$emit('update:modelValue', $event.target.value)"
            :disabled="isConfirmedEmployeesEmpty && isConfirmedProjectEmpty"
            ref="initialDateFromRef"
            @close="openDateTo"
          />
          <SPADatepicker
            label="Date To"
            v-model="initialDateTo.date"
            :startDate="initialDateFrom.date"
            standardFormat
            checkIfIsWithin31Day
            @hasError="initialDateTo.isError = $event"
            @input="$emit('update:modelValue', $event.target.value)"
            @update:modelValue="initialDateTo.date"
            :disabled="isConfirmedEmployeesEmpty && isConfirmedProjectEmpty"
            ref="initialDateToRef"
          />
        </div>
      </div>
      <div class="drawer-reports-search">
        <SPAButton id="SearchFilterBtn" @click="handleSearchFilter" :disabled="isSearchBtnDisabled"><IconMagnifyingGlass class="icon" id="magnifying" />Search</SPAButton>
        <SPAButton class="filter-reset-btn" color="tertiary" v-if="showResetBtn" @click="handleReset">Reset</SPAButton>
      </div>
    </div>
  </MDBSideNav>
</template>

<script>
import { MDBSideNav } from 'mdb-vue-ui-kit';
import { storeToRefs } from 'pinia';
import { useProjectTaggingStore } from '../../../../../stores/projectTagging';
import SPAInput from '../../../../../components/SPAInput.vue';
import SPADatepicker from '../../../../../components/SPADatepicker.vue';
import { computed, reactive, ref, watch } from 'vue';
import SPAButton from '../../../../../components/SPAButton.vue';
import IconMagnifyingGlass from '~icons/ph/magnifying-glass';
import { useDebounceFn } from '@vueuse/core';
import { isProjectLogsDateRangeValid } from '../../../Business/ProjectTaggingBusiness';
import { parseDate } from '../../../../../helpers/dateParser';
export default {
  components: {
    MDBSideNav,
    SPAInput,
    SPADatepicker,
    SPAButton,
    IconMagnifyingGlass,
  },
  setup() {
    const projectTagging = useProjectTaggingStore();
    const {
      drawerReportState,
      confirmedProject,
      confirmedEmployees,
      viewProjectListDrawer,
      viewEmployeeListDrawer,
      reportsPagination,
      selectedEmployees,
      showEmployees,
      isConfirmedProjectEmpty,
      isConfirmedEmployeesEmpty,
      isFiltersUpdated,
      reportsHeaders,
    } = storeToRefs(projectTagging);
    const handleDrawerClose = () => {
      drawerReportState.value = false;
    };
    const initialDateFrom = reactive({
      date: '',
      isError: false,
    });
    const initialDateTo = reactive({
      date: '',
      isError: false,
    });

    watch(initialDateFrom, (newVal) => {
      const filteredDateFrom = parseDate(reportsHeaders.value[0]);
      isFiltersUpdated.value = newVal.isError || (reportsHeaders.value && filteredDateFrom != newVal.date);
    });
    watch(initialDateTo, (newVal) => {
      const filteredDateTo = parseDate(reportsHeaders.value[reportsHeaders.value.length - 1]);
      isFiltersUpdated.value = newVal.isError || (reportsHeaders.value && filteredDateTo != newVal.date);
    });
    const isDateRangeValid = computed(() => isProjectLogsDateRangeValid(initialDateFrom.date, initialDateTo.date));
    const isInitialDateFromInvalid = computed(() => initialDateFrom.date === '' || initialDateFrom.isError);
    const isInitialDateToInvalid = computed(() => initialDateFrom.date === '' || initialDateTo.isError);
    const isSearchBtnDisabled = computed(
      () =>
        isConfirmedProjectEmpty.value ||
        (isConfirmedEmployeesEmpty.value && confirmedProject.value.assigneesCount < 1) ||
        isInitialDateFromInvalid.value ||
        isInitialDateToInvalid.value ||
        !isDateRangeValid.value
    );
    const showResetBtn = computed(() => !isConfirmedProjectEmpty.value);
    const showProject = computed(() => (!isConfirmedProjectEmpty.value ? confirmedProject.value.name : ''));
    watch(confirmedProject, (newProject) => {
      if (newProject) {
        confirmedEmployees.value = [];
      }
    });
    const handleDrawerClick = (drawerType) => {
      if (drawerType == 'project') {
        debouncedDrawer();
        viewProjectListDrawer.value = !viewProjectListDrawer.value;
      } else {
        toggleSelectEmployeeDrawer();
      }
    };
    const toggleSelectEmployeeDrawer = () => {
      if (!isConfirmedProjectEmpty.value && !isAssigneesCountZero.value) {
        debouncedDrawer();
        viewEmployeeListDrawer.value = !viewEmployeeListDrawer.value;

        if (confirmedEmployees.value.length != 0) {
          selectedEmployees.value = [...confirmedEmployees.value];
        }
      }
    };
    const empIdsArray = ref([]);
    const createEmpIdsArray = () => {
      empIdsArray.value = confirmedEmployees.value.map((el) => el.employeeId);
    };

    const handleSearchFilter = async () => {
      if (!isDateRangeValid.value) {
        return;
      }
      createEmpIdsArray();
      const params = {
        EmpIds: empIdsArray.value,
        DateFrom: initialDateFrom.date,
        DateTo: initialDateTo.date,
        PageNumber: 1,
        RowsPerPage: reportsPagination.value.itemsPerPage,
      };

      await projectTagging.getProjectLogs(params);
    };

    const initialDateFromRef = ref(null);
    const initialDateToRef = ref(null);
    const handleReset = () => {
      confirmedProject.value = {};
      confirmedEmployees.value = [];
      selectedEmployees.value = [];
    };
    const debouncedDrawer = useDebounceFn(() => {
      if (viewProjectListDrawer.value && viewEmployeeListDrawer.value) drawerReportState.value = !drawerReportState.value;
    }, 1000);

    const isAssigneesCountZero = computed(() => confirmedProject.value.assigneesCount == 0);

    return {
      drawerReportState,
      handleDrawerClose,
      initialDateFrom,
      initialDateTo,
      confirmedProject,
      confirmedEmployees,
      handleDrawerClick,
      isConfirmedProjectEmpty,
      isConfirmedEmployeesEmpty,
      showProject,
      showEmployees,
      handleReset,
      handleSearchFilter,
      isInitialDateFromInvalid,
      isInitialDateToInvalid,
      isSearchBtnDisabled,
      showResetBtn,
      initialDateFromRef,
      initialDateToRef,
      isAssigneesCountZero,
    };
  },
};
</script>

<style lang="scss" scoped>
.drawer-container {
  display: grid;
  line-height: 24px;
  flex-direction: column;
  grid-template-rows: auto 1fr auto;
  height: 100%;
  border-left: 1px solid $neutral-40;
  .drawer-header {
    font-weight: 500;
    color: $neutral-800;
    display: flex;
    justify-content: space-between;
    padding: 14px 16px;
    border-bottom: 1px solid $neutral-40;
    align-items: center;
    .SproutIcon-cross-regular {
      color: $neutral-400;
    }

    .SproutIcon-cross-regular:hover {
      cursor: pointer;
      color: $green-300;
    }
  }
  .drawer-filter,
  .drawer-date-range {
    padding: 16px 24px;
  }
  .drawer-filter {
    display: grid;
    row-gap: 16px;
    :deep(.form-outline) .form-control[readonly] {
      background-color: transparent;
    }
  }
  .drawer-date-range {
    color: $neutral-500;
    font-size: 14px;
    font-weight: 500;
    display: grid;
    row-gap: 8px;
  }
  .drawer-reports-search {
    display: grid;
    row-gap: 8px;
    padding: 16px;
    border-top: 1px solid $neutral-40;
    #SearchFilterBtn,
    .filter-reset-btn {
      padding: 16px;
      width: 100%;
    }
    #magnifying {
      margin-right: 8px;
    }
    .filter-reset-btn {
      text-align: center;
    }
  }
}
.datepicker-table {
  .datepicker-cell {
    border: 1px solid $neutral-20;
  }
}
</style>
